import { useEffect, useRef, useState } from 'react'

/**
 * ── Centralized IntersectionObserver hooks ──────────────────────────────
 * Every IntersectionObserver in the app should be created through one of
 * the hooks in this file. Components keep their own animation/effect logic;
 * these hooks only handle observing the DOM and reporting visibility.
 */

/**
 * useInView
 * Repeatable in/out toggle for a single element — true while intersecting,
 * false once it leaves. Use for effects that should pause/resume with
 * visibility (autoplay carousels, scroll-linked effects, etc.).
 *
 * @param {object}  [options] - native IntersectionObserver options (threshold, rootMargin, root)
 * @returns {[React.RefObject, boolean]} [ref to attach, isInView]
 */
export function useInView(options) {
  const ref = useRef(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      options
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.threshold, options?.rootMargin, options?.root])

  return [ref, isInView]
}

/**
 * useInViewOnce
 * Fires a callback the first time an element enters the viewport, then
 * disconnects. Honors prefers-reduced-motion by firing immediately.
 * Use for one-shot entrance animations (counters, reveals).
 *
 * @param {Function} onEnter  - called once when the element first intersects
 * @param {object}   [options] - native IntersectionObserver options
 * @param {any[]}    [deps]    - extra deps that should re-arm the observer
 * @returns {React.RefObject} ref to attach
 */
export function useInViewOnce(onEnter, options, deps = []) {
  const ref = useRef(null)
  const startedRef = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onEnter()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true
          onEnter()
          observer.disconnect()
        }
      },
      options
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return ref
}

/**
 * useScrollSpy
 * Tracks which section ID is currently in the viewport, for jump-nav
 * "active link" highlighting.
 *
 * @param {string[]} sectionIds - array of section IDs to watch
 * @param {object}   [options]  - native IntersectionObserver options
 *                                (e.g. { rootMargin: '-20% 0px -70% 0px' })
 * @returns {string} the currently active section ID
 */
export function useScrollSpy(sectionIds, options = {}) {
  const [activeId, setActiveId] = useState(sectionIds[0] ?? '')

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        })
      },
      options
    )

    sectionIds.forEach(id => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds, options.rootMargin, options.threshold])

  return activeId
}

/**
 * useTextReveal
 * Observes all .text-reveal elements in the DOM and toggles .is-visible
 * whenever they enter or leave the viewport. Call once at the app root.
 * Re-observes new elements added after route changes via MutationObserver.
 */
/* ── Shared IntersectionObserver options for reveal hooks ── */
const REVEAL_IO_OPTIONS = { rootMargin: '-10% 0px -10% 0px', threshold: 0 }
export function useTextReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-visible', entry.isIntersecting)
        })
      },
      REVEAL_IO_OPTIONS
    )

    const observe = () => {
      document.querySelectorAll('.text-reveal').forEach((el) => observer.observe(el))
    }
    observe()

    // Re-observe on route changes — small delay lets React render first
    const mutationObserver = new MutationObserver(() => {
      document.querySelectorAll('.text-reveal:not([data-observed])').forEach((el) => {
        el.setAttribute('data-observed', '1')
        observer.observe(el)
      })
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
    }
  }, [])
}



/**
 * useTextRevealLines
 * Line-by-line 3D flip for elements with .text-reveal-lines class.
 * - Splits text into visual lines by comparing word offsetTop
 * - Each line animates with 150ms stagger
 * - Recalculates on window resize (stays visible if already visible)
 * - Replays every time element enters/leaves the viewport
 */
export function useTextRevealLines() {
  useEffect(() => {


    function splitIntoLines(el) {
      /* Save plain text only — safer than innerHTML */
      if (!el.dataset.trlOriginal) {
        el.dataset.trlOriginal = el.textContent
      }

      const wasVisible = el.classList.contains('is-visible-lines')

      /* Reset to plain text before re-splitting */
      el.textContent = el.dataset.trlOriginal

      /* Wrap every word in a temp span to measure position */
      const words = el.textContent.trim().split(/\s+/)
      el.innerHTML = words
        .map((w) => `<span class="trl-word" style="display:inline-block;white-space:nowrap">${w}</span>`)
        .join(' ')

      /* Group words by visual line using offsetTop */
      const wordEls = Array.from(el.querySelectorAll('.trl-word'))
      const lines = []
      let currentLine = []
      let currentTop = null

      wordEls.forEach((word) => {
        const top = word.offsetTop
        if (currentTop === null) currentTop = top
        if (Math.abs(top - currentTop) > 4) {
          lines.push(currentLine)
          currentLine = []
          currentTop = top
        }
        currentLine.push(word)
      })
      if (currentLine.length) lines.push(currentLine)

      /* Rebuild with animated line spans */
      el.innerHTML = ''
      lines.forEach((lineWords, lineIndex) => {
        const lineWrap = document.createElement('span')
        lineWrap.className = 'trl-line-wrap'
        lineWrap.style.cssText = 'display:block; overflow:hidden;'

        const lineInner = document.createElement('span')
        lineInner.className = 'trl-line'
        lineInner.style.transitionDelay = `${lineIndex * 150}ms`
        lineInner.textContent = lineWords.map((w) => w.textContent).join(' ')

        lineWrap.appendChild(lineInner)
        el.appendChild(lineWrap)
      })

      /* Stay visible after resize */
      if (wasVisible) {
        el.classList.add('is-visible-lines')
        el.querySelectorAll('.trl-line').forEach((line) => {
          line.style.opacity = '1'
          line.style.transform = 'rotateX(0deg)'
        })
      }
    }

    /* ── IntersectionObserver ── */
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target
        if (entry.isIntersecting) {
          el.classList.add('is-visible-lines')
          el.querySelectorAll('.trl-line').forEach((line) => {
            line.style.opacity = '1'
            line.style.transform = 'rotateX(0deg)'
          })
        } else {
          el.classList.remove('is-visible-lines')
          el.querySelectorAll('.trl-line').forEach((line) => {
            line.style.opacity = '0'
            line.style.transform = 'rotateX(-120deg)'
          })
        }
      })
    }, REVEAL_IO_OPTIONS)

    /* ── Observe a single element ── */
    function observeEl(el) {
      splitIntoLines(el)
      observer.observe(el)
    }

    /* ── Initial scan — delay lets React finish rendering ── */
    const initTimer = setTimeout(() => {
      document.querySelectorAll('h1:not(.no-reveal), h2:not(.no-reveal), h3:not(.no-reveal)').forEach(observeEl)
    }, 100)

    /* ── MutationObserver — new elements after route changes ── */
    const mutationObserver = new MutationObserver(() => {
    document.querySelectorAll('h1:not(.no-reveal), h2:not(.no-reveal), h3:not(.no-reveal)').forEach((el) => {
      if (!el.querySelector('.trl-line')) {
        observeEl(el)
      }
    })
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    /* ── Resize ── */
    let resizeTimer = null
    const onResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        document.querySelectorAll('h1:not(.no-reveal), h2:not(.no-reveal), h3:not(.no-reveal)').forEach(splitIntoLines)
      }, 150)
    }
    window.addEventListener('resize', onResize)

    return () => {
      clearTimeout(initTimer)
      clearTimeout(resizeTimer)
      observer.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [])
}


/**
 * useTreeAnimation
 * Moves a dot from the tree root along each SVG branch path in turn.
 * Pauses while the tree is off-screen or hidden, and does not animate
 * for users who prefer reduced motion.
 * @param {string}   dotId     id of the travelling <circle>
 * @param {string[]} branchIds ids of the branch <path>s, in travel order (keep this array stable)
 * @param {number}   startX    x of the root point (y is always 0)
 */
const TREE_DOT_DURATION = 1200 // ms to travel one branch
const TREE_DOT_PAUSE = 400     // ms to rest at the end of a branch
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)

export function useTreeAnimation(dotId, branchIds, startX = 600) {
  useEffect(() => {
    const dot = document.getElementById(dotId)
    const paths = branchIds.map((id) => document.getElementById(id))
    const svg = dot?.ownerSVGElement
    if (!dot || !svg || paths.some((p) => !p)) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let branch = 0
    let startTime = null
    let rafId = 0
    let timerId = 0

    const moveTo = (x, y) => {
      dot.setAttribute('cx', x)
      dot.setAttribute('cy', y)
    }

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp
      const elapsed = timestamp - startTime
      const path = paths[branch]
      const length = path.getTotalLength()

      if (elapsed < TREE_DOT_DURATION) {
        const point = path.getPointAtLength(easeInOut(elapsed / TREE_DOT_DURATION) * length)
        moveTo(point.x, point.y)
        rafId = requestAnimationFrame(step)
        return
      }

      const end = path.getPointAtLength(length)
      moveTo(end.x, end.y)
      timerId = setTimeout(() => {
        moveTo(startX, 0)
        branch = (branch + 1) % paths.length
        startTime = null
        rafId = requestAnimationFrame(step)
      }, TREE_DOT_PAUSE)
    }

    const start = () => {
      startTime = null
      moveTo(startX, 0)
      rafId = requestAnimationFrame(step)
    }

    const stop = () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timerId)
    }

    const observer = new IntersectionObserver(([entry]) => {
      stop()
      if (entry.isIntersecting) start()
    })
    observer.observe(svg)

    return () => {
      observer.disconnect()
      stop()
    }
  }, [dotId, branchIds, startX])
}